import { useEffect, useState } from "react";
import {
  App,
  Cascader,
  Flex,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";

import api from "@/apis/supplier";
import { PriceHis } from "@/types/sales";
import useDataList from "@/hooks/useDataList";
import { CommonListParams } from "@/types/common";
import dayjs from "dayjs";
import { SupplierPrice } from "@/types/purchase";
import { Product } from "@/types/product";
import { dateFormat } from "@/utils/format";

const { Text } = Typography;

type SelectValue = Array<string>;

interface SCurrencySelectProps {
  supplierId: string;
  value?: SelectValue;
  onChange?: (ids: Array<string>, value: any) => void;
  defaultList?: Array<PriceHis>;
  disabled?: boolean;
  disabledFetch?: boolean; // 新增：禁用自动加载数据
}

interface LadderPriceOption {
  label: string;
  value: string;
  product: Product | null;
  expiredAt: number;
  isProduct: boolean;
  count?: number;
  discount?: string;
  children?: Array<LadderPriceOption>;
  price?: string;
}

const SelectLaddersPrice = ({
  value,
  onChange,
  supplierId,
  disabled,
  defaultList = [],
  disabledFetch = false,
}: SCurrencySelectProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  // const { update: updatePrice, loading: updateLoading } = useDataUpdate<[PriceAdd, string]>({ api: priceApis.update });
  const [list, , listLoading, { fetchList, updateItem }] = useDataList<SupplierPrice,
    [string, CommonListParams?]
  >({ fetchApi: api.supplierPricesFetch });

  const [detail, setDetail] = useState<PriceHis | null>(null);

  const options: Array<LadderPriceOption> = list?.map((item) => ({
    label: item.product?.name,
    value: item.product?.id,
    product: item.product,
    expiredAt: item.expiredAt,
    isProduct: true,
    children: item.ladders?.map((ladder) => ({
      label: ladder.price,
      value: ladder.id,
      discount: ladder.discount,
      product: item.product,
      expiredAt: item.expiredAt,
      isProduct: false,
      count: ladder.count
    })),
  }));

  useEffect(() => {
    // 只有在不禁用加载且有 supplierId 时才调用 API
    if (!disabledFetch && supplierId) {
      fetchList(supplierId);
    }
  }, [supplierId, disabledFetch]);

  const getProductRender = (item: LadderPriceOption) => {
    return <Tooltip
      title={
        <div>
          <div>{t("product.code")}: {item?.product?.code ?? ""}</div>
          <div>{t("product.name")}: {item?.product?.name ?? ""}</div>
          <div>{t("product.brand1")}:{item?.product?.model?.toLocaleUpperCase() ?? ""}</div>
          <div>{t("product.brand2")}:{item?.product?.t1?.toLocaleUpperCase() ?? ""}</div>
          <div>{`${t("purchase.expiredAt")}: ${item?.expiredAt ? dateFormat(dayjs(item?.expiredAt)) : ""}`}</div>
        </div>
      }
    >
      <Flex
        gap={10}
        style={{ width: "100%" }}
        align="center"
        justify="space-between"
      >
        <Text style={{ width: "20%" }} ellipsis>
          {item?.product?.name ?? ""}
        </Text>
        <Text style={{ width: "20%" }} ellipsis>
          {item?.product?.code ?? ""}
        </Text>
        <Text style={{ width: "60%" }} ellipsis>
          {item?.product?.model?.toLocaleUpperCase() ?? ""}
        </Text>
      </Flex>
    </Tooltip>
  }

  return (
    <Cascader
      disabled={disabled}
      value={value}
      options={options}
      optionRender={option => option.isProduct ? getProductRender(option)
        : (
          <Flex gap={10} style={{ width: '100%' }} justify='space-around'>
            <Tooltip title={t('purchase.ladderPrice')}><span title="">{option.label}</span></Tooltip>
            <Tooltip title={t('purchase.ladderPriceCount')}><Tag color='blue' title="">{option.count}</Tag></Tooltip>
          </Flex>
        )}
      onDropdownVisibleChange={(open) => {
        if (open) {
          if (!supplierId) {
            notification.error({
              placement: 'top',
              message: t('purchase.plSupplier'),
            });
          }
        }
      }}
      placeholder={t("purchase.plProductPrice")}
      showSearch
      onChange={(val, info) => {
        if (val?.length > 1) {
          const [, priceInfo] = info
          const data = {
            expiredAt: priceInfo.expiredAt,
            product: priceInfo.product,
            productId: priceInfo.product?.id,
            price: priceInfo.label,
            count: priceInfo.count,
            discount: priceInfo.discount,
            priceId: val[1]
          }
          onChange && onChange(val, data);
        } else {
          onChange && onChange([], undefined);
        }
      }}
      style={{ width: "100%" }}
    />
  );
};

export default SelectLaddersPrice;
